import { ERR_INVALID_ARGUMENT } from "../error";
import { Lock } from "./Lock";
/**
 * Executes pTask sequentially for all elements of pArray.
 * If pTask returns false, the execution is aborted.
 * Returns false if execution was aborted.
 */
export function forEach(pArray, pReverseOrder, pTask) {
    if (pArray == null)
        return (Promise.reject(new Error(ERR_INVALID_ARGUMENT)));
    if (pArray.length <= 0)
        return (Promise.resolve(true));
    const processElement = (pLast, pIndex) => {
        const tPromise = pLast.then((pContinue) => {
            if (!pContinue)
                return (false);
            else
                return (pTask(pIndex, pArray[pIndex]));
        });
        return (tPromise);
    };
    let tPromise = Promise.resolve(true);
    if (pReverseOrder) {
        for (let i = pArray.length - 1; i >= 0; --i)
            tPromise = processElement(tPromise, i);
    }
    else {
        for (let i = 0; i < pArray.length; ++i)
            tPromise = processElement(tPromise, i);
    }
    return (tPromise);
}
/**
 * Executes pTask parallel for all elements of pArray.
 * Order of execution is random.
 * Execution is aborted in case of error.
 * The maximum number of concurrent tasks can be defined with the `pMaxConcurrentTasks` parameter.
 * If it is 0 or undefined, the maximum number of concurrent tasks will be unlimited (default: undefined).
 */
export function forEachParallel(pArray, pTask, pMaxConcurrentTasks) {
    if (pArray == null)
        return (Promise.reject(new Error(ERR_INVALID_ARGUMENT)));
    if (pArray.length <= 0)
        return (Promise.resolve(new Array()));
    let tPromises;
    if (pMaxConcurrentTasks == null || pMaxConcurrentTasks < 1 || pArray.length <= pMaxConcurrentTasks) {
        //unlimited concurrent tasks
        tPromises = pArray.map((pElement, pIndex) => pTask(pIndex, pElement));
    }
    else {
        //limit number of concurrent tasks
        const tLock = new Lock(pMaxConcurrentTasks);
        tPromises = pArray.map(async (pElement, pIndex) => {
            const tLockContext = await tLock.acquireLock();
            try {
                const tResult = await pTask(pIndex, pElement);
                return (tResult);
            }
            finally {
                tLockContext.unlock();
            }
        });
    }
    return (Promise.all(tPromises));
}
/** Returns a promise which resolves after the given timeout. Note: uses `setTimeout` internally. */
export function delay(pTimeout) {
    return (new Promise((resolve) => {
        setTimeout(resolve, pTimeout);
    }));
}
/** Returns a promise which resolves with the delay time after `requestAnimationFrame`. */
export function delayForAnimation() {
    return (new Promise((resolve) => {
        requestAnimationFrame(resolve);
    }));
}
